import { useState } from "react";
import { CalendarCheck, ClipboardCheck, Upload } from "lucide-react";
import { todayET } from "@/lib/dates";

const MAX_BYTES = 25 * 1024 * 1024;

export const StatusBanner = ({ item }) => {
  if (item.status === "Pending TAG review") {
    return (
      <section className="provider-card provider-status-banner review" data-testid="provider-status-banner" role="status">
        <p className="eyebrow">STATUS</p>
        <strong>Report submitted · waiting on TAG review</strong>
        <span className="muted">Uploaded successfully. TAG will approve the report or send it back with comments; you can replace the file below until TAG completes the review.</span>
      </section>
    );
  }
  if (item.status === "Returned for revisions") {
    return (
      <section className="provider-card provider-status-banner returned" data-testid="provider-status-banner">
        <p className="eyebrow">STATUS</p>
        <strong>Returned for revisions</strong>
        {item.return_comment && <p className="return-note" data-testid="provider-return-comment">TAG: {item.return_comment}</p>}
      </section>
    );
  }
  return null;
};

export const StepActions = ({ item, busy, onSchedule, onTestingComplete }) => {
  const [scheduling, setScheduling] = useState(false);
  const [date, setDate] = useState(item.scheduled_date || todayET());
  if (!item.actions?.schedule && !item.actions?.testing_complete) return null;
  return (
    <section className="provider-card provider-steps" data-testid="provider-step-actions">
      <p className="eyebrow">YOUR STEPS</p>
      <div className="provider-step-buttons">
        {item.actions.schedule && (
          <button className={item.status === "Assigned" ? "primary-button" : "ghost-button"} data-testid="provider-step-schedule" type="button" disabled={busy} onClick={() => setScheduling((value) => !value)}>
            <CalendarCheck size={15} /> {item.scheduled_date ? "Reschedule testing" : "Testing scheduled"}
          </button>
        )}
        {item.actions.testing_complete && (
          <button className={item.status === "Testing scheduled" ? "primary-button" : "ghost-button"} data-testid="provider-step-testing-complete" type="button" disabled={busy} onClick={() => { const early = item.scheduled_date && item.scheduled_date > todayET(); if (window.confirm(early ? `Testing was scheduled for ${new Date(`${item.scheduled_date}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" })}, which is still ahead. Mark testing complete anyway? The school will see “Testing complete” on its tracker.` : "Mark testing complete for this student? The school will see “Testing complete” on its tracker.")) onTestingComplete(); }}>
            <ClipboardCheck size={15} /> Testing complete
          </button>
        )}
      </div>
      {scheduling && (
        <form className="provider-schedule-form" data-testid="provider-schedule-form" onSubmit={(event) => { event.preventDefault(); onSchedule(date).then((ok) => ok && setScheduling(false)); }}>
          <label><span className="field-label">Testing date</span><input data-testid="provider-schedule-date" type="date" required value={date} onChange={(event) => setDate(event.target.value)} /></label>
          <button className="primary-button" data-testid="provider-schedule-save" type="submit" disabled={busy || !date}>{busy ? "Saving…" : "Save testing date"}</button>
        </form>
      )}
    </section>
  );
};

export const ReportUpload = ({ item, busy, onUpload, onInvalid }) => {
  const [file, setFile] = useState(null);
  if (!item.actions?.upload) return null;
  const heading = item.status === "Returned for revisions" ? "Revise and re-upload the report" : item.status === "Pending TAG review" ? "Replace the submitted report" : "Upload the report";
  const choose = (event) => {
    const next = event.target.files?.[0] || null;
    if (next && !/\.(pdf|docx)$/i.test(next.name)) { onInvalid(`${next.name} isn't a PDF or Word (.docx) file. Export the report as PDF or DOCX and choose it again.`); setFile(null); return; }
    if (next && next.size > MAX_BYTES) { onInvalid(`${next.name} is ${(next.size / (1024 * 1024)).toFixed(1)} MB; reports must be 25 MB or smaller.`); setFile(null); return; }
    if (next && next.size === 0) { onInvalid(`${next.name} is empty (0 bytes). Re-export the report and choose it again.`); setFile(null); return; }
    onInvalid("");
    setFile(next);
  };
  return (
    <form className="provider-card provider-upload" data-testid="provider-upload-form" onSubmit={(event) => { event.preventDefault(); if (file) onUpload(file).then((ok) => ok && setFile(null)); }}>
      <p className="eyebrow">{item.status === "Pending TAG review" ? "REPORT ON FILE" : "YOUR NEXT STEP"}</p>
      <h2>{heading}</h2>
      <label className="consent-upload"><Upload size={15} /><span>{file ? file.name : "Choose PDF, Word file or photo"}</span><input data-testid="provider-report-file" type="file" accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/*" onChange={choose} /></label>
      <button className="primary-button" data-testid="provider-report-submit" type="submit" disabled={!file || busy}>{busy ? "Uploading…" : `Submit ${item.kind_word} report`}</button>
      <span className="muted">PDF or DOCX, up to 25 MB. TAG is notified in its next digest and the school sees “Report in review”.</span>
    </form>
  );
};
